// @ts-strict-ignore
import React, { useContext, useEffect } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import {
  Badge,
  Banner,
  Box,
  ButtonSize,
  Icon,
  IconName,
  Modal,
} from '@usealloy/component-library';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { selectShouldRenderCriticalNodePath } from '~/dashboard/views/workflows/store/redux/selectors';
import {
  StyledHeader,
  StyledSectionHeader,
  StyledTagDescription,
  StyledToggleButton,
} from './styles';
import TagRule from './TagRule';
import { useRuleExplainability } from './useRuleExplainability';
import EvidencesTable from './EvidencesTable';
import { UserContext } from '~/dashboard/context';
import type { Dimension } from '~/application-review/redux/types';
import { EditorContainerType } from 'flow/store/redux/constants';
import {
  resetNodeAndConnectorState,
  setEditorContainerType,
  setSelectedEvidenceThresholdId,
} from 'flow/store/redux/slice';
import { selectSelectedEvidenceThresholdId } from 'flow/store/redux/selectors/workflowFeatures';
import { toggleShouldRenderCriticalNodePath } from 'flow/store/redux/thunks';
import { EditorAccessProvider } from 'flow/editor/context';

type TagRuleModalProps = {
  actionName?: string;
  outcome?: string | null;
  tagDescription?: string;
  closeModal: () => void;
  entityToken: string;
  evaluationTags: Array<{ tag_name: string }> | null;
  evaluationToken: string;
  isPortfolioEvaluation: boolean;
  dimensions?: Dimension[];
  workflowToken: string | null;
  workflowVersion: number | null;
};

// Defaulting dimensions to a new array inline causes an infinite update loop in some cases.
// Defaulting dimensions to a pre-defined initialDimensions fixes this issue.
const initialDimensions = [];

const TagRuleModal = ({
  actionName,
  outcome = null,
  tagDescription,
  closeModal,
  entityToken,
  evaluationTags,
  evaluationToken,
  isPortfolioEvaluation,
  dimensions = initialDimensions,
  workflowToken,
  workflowVersion,
}: TagRuleModalProps) => {
  const { featureFlags } = useContext(UserContext);

  const dispatch = useAppDispatch();
  const thresholdId = useAppSelector(selectSelectedEvidenceThresholdId);
  const shouldRenderCriticalNodePath = useAppSelector(selectShouldRenderCriticalNodePath);

  const {
    workflowData,
    canViewRules,
    shouldInitializeWorkflow,
    workflowLoading,
    actionToShow,
    legacyActionId,
    thresholdResults,
    thresholdResultsLoading,
    thresholdResultsError,
    isRuleExplainabilityDataLoaded,
  } = useRuleExplainability({
    actionName,
    outcome,
    entityToken,
    evaluationTags,
    evaluationToken,
    isPortfolioEvaluation,
    dimensions,
    workflowToken,
    workflowVersion,
    containerType: EditorContainerType.MODAL,
  });

  const criticalPathViewToggleButton = (
    <StyledToggleButton
      data-pendo-anchor="evaluation-explainability-critical-path-toggle"
      icon={shouldRenderCriticalNodePath ? IconName.View : IconName.EyeSlash}
      onClick={() => dispatch(toggleShouldRenderCriticalNodePath())}
      size={ButtonSize.xsmall}
    >
      {
        shouldRenderCriticalNodePath
          ? 'Show Unset Tags'
          : 'Hide Unset Tags'
      }
    </StyledToggleButton>
  );

  const Header = (
    <StyledHeader justifyContent="flex-start" alignItems="center" gap={2}>
      {featureFlags?.ruleExplainabilityPhase >= 4
        ? (
          <>
            <Icon icon={IconName.Workflows} data-pendo-anchor="evaluation-explainability-read-only-workflow" />
            {workflowData && !shouldInitializeWorkflow && (
              <>
                {workflowData.name}
                <Badge outline style={{ borderColor: 'var(--cl-gray-600)' }}>{`v${workflowData.publicVersion}`}</Badge>
              </>
            )}
            {featureFlags?.ruleExplainabilityPhase >= 5 && criticalPathViewToggleButton}
          </>
        )
        : actionName}
    </StyledHeader>
  );

  useEffect(() => {
    if (actionName && tagDescription) datadogRum.addAction('explainability-hasTagDescription', { actionName });
  }, [actionName, tagDescription]);
  useEffect(() => {
    if (actionName && thresholdResultsError) datadogRum.addAction('explainability-hasThresholdResultsError', { actionName });
  }, [actionName, thresholdResultsError]);

  return (
    <Modal
      isOpen
      header={Header}
      // Modal has no footer so this is modal spacing from viewport (64px) + modal header height (72px)
      maxHeight={136}
      onClose={() => {
        dispatch(resetNodeAndConnectorState());
        dispatch(setEditorContainerType(EditorContainerType.PAGE));
        dispatch(setSelectedEvidenceThresholdId(null));
        closeModal();
      }}
      style={{
        overflowX: 'hidden',
      }}
      size={featureFlags?.ruleExplainabilityPhase >= 4 ? 'xl' : 'medium'}
    >
      <EditorAccessProvider>
        <Box
          direction="column"
          alignItems="flex-start"
          gap={featureFlags?.ruleExplainabilityPhase >= 4 ? 0 : 5}
          style={{
            height: featureFlags?.ruleExplainabilityPhase >= 4 ? '100%' : 'auto',
          }}
        >
          {
            !!tagDescription && featureFlags?.ruleExplainabilityPhase < 4 && (
              <Box direction="column" alignItems="flex-start" gap={3}>
                <StyledSectionHeader size={1}>
                  Description
                </StyledSectionHeader>
                <StyledTagDescription>
                  {tagDescription}
                </StyledTagDescription>
              </Box>
            )
          }
          <Banner
            open={!!thresholdResultsError}
            outline
            borderColor="var(--cl-yellow-300)"
            variant="info"
          >
            Outputs for this rule are unavailable or do not exist
          </Banner>
          <Banner
            open={!canViewRules}
            outline
            borderColor="var(--cl-red-300)"
            variant="error"
          >
            {'You don\'t have permission to view this rule'}
          </Banner>
          <TagRule
            action={actionToShow}
            thresholdResults={thresholdResults}
            loading={(
              shouldInitializeWorkflow
              || thresholdResultsLoading
              || workflowLoading
              || !isRuleExplainabilityDataLoaded
            )}
            canViewRules={canViewRules}
          />
          <div style={{ width: '100%' }}>
            {thresholdId !== null && featureFlags?.ruleExplainabilityPhase < 4 && (
              <EvidencesTable
                thresholdId={thresholdId}
                actionId={legacyActionId}
                entityToken={entityToken}
                evaluationToken={evaluationToken}
                isPortfolioEvaluation={isPortfolioEvaluation}
                dimensions={dimensions}
              />
            )}
          </div>
        </Box>
      </EditorAccessProvider>
    </Modal>
  );
};

export default TagRuleModal;
